import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ViewMode } from './chrome/AppHeader.tsx';

interface LayoutProps {
    editor: ReactNode;
    preview: ReactNode;
    viewMode: ViewMode;
}

const STORAGE_KEY = 'nl-editor-pct';
const DEFAULT_PCT = 50;
const MIN_PCT = 20;
const MAX_PCT = 80;

function clampPct(v: number) {
    return Math.min(MAX_PCT, Math.max(MIN_PCT, v));
}

export function Layout({ editor, preview, viewMode }: LayoutProps) {
    const [editorPct, setEditorPct] = useState<number>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const v = Number(stored);
                if (v >= MIN_PCT && v <= MAX_PCT) return v;
            }
        } catch {
            // storage unavailable
        }
        return DEFAULT_PCT;
    });

    const layoutRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, String(editorPct));
        } catch {
            // storage unavailable
        }
    }, [editorPct]);

    function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
        e.currentTarget.setPointerCapture(e.pointerId);
        draggingRef.current = true;
    }

    function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
        if (!draggingRef.current || !layoutRef.current) return;
        const rect = layoutRef.current.getBoundingClientRect();
        const pct = ((e.clientX - rect.left) / rect.width) * 100;
        setEditorPct(clampPct(pct));
    }

    function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
        e.currentTarget.releasePointerCapture(e.pointerId);
        draggingRef.current = false;
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setEditorPct((v) => clampPct(v - 2));
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            setEditorPct((v) => clampPct(v + 2));
        }
    }

    const editorHidden = viewMode === 'diagram';
    const previewHidden = viewMode === 'editor';
    const isBoth = viewMode === 'both';

    return (
        <div
            className={`layout${isBoth ? ' layout--both' : ''}`}
            ref={layoutRef}
            style={{ '--editor-basis': `${editorPct}%` } as CSSProperties}
        >
            {/* Editor pane — both panes always in DOM for CodeMirror state preservation */}
            <section
                id='pane-editor'
                aria-label='Editor'
                className={`pane pane--editor${editorHidden ? ' pane--hidden' : ''}`}
            >
                {editor}
            </section>

            {/* Splitter — shown only in 'both' mode on wide screens (CSS media query) */}
            {/* biome-ignore lint/a11y/useSemanticElements: draggable split handle */}
            <div
                className='splitter'
                role='separator'
                aria-orientation='vertical'
                aria-label='Resize editor and preview'
                aria-valuenow={Math.round(editorPct)}
                aria-valuemin={MIN_PCT}
                aria-valuemax={MAX_PCT}
                tabIndex={0}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={onKeyDown}
            />

            {/* Preview pane */}
            <section
                id='pane-preview'
                aria-label='Diagram preview'
                className={`pane pane--preview${previewHidden ? ' pane--hidden' : ''}`}
            >
                {preview}
            </section>
        </div>
    );
}
